import $ from 'jquery';
import { Component } from '@mycolorway/tao';
import { closest } from 'next/javascripts/modules/dom';
import { throttle } from 'next/javascripts/modules/utils';
import baseMixin from './base';
import 'next/stylesheets/components/grid/cells/todo-title-cell.scss';

Component('tr-grid-todo-title-cell', {
  mixins: [baseMixin],

  properties: {
    wrapContent: {
      type: Boolean,
      default: false,
    },

    completed: {
      type: Boolean,
      default: false,
    },

    running: {
      type: Boolean,
      default: false,
    },

    lineThrough: {
      type: Boolean,
      default: false,
    },

    showCloser: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
      observer(disabled) {
        if (this.contentInput) this.contentInput.disabled = disabled;
      },
    },

    value: {
      get() {
        return this.contentInput ? this.contentInput.value : '';
      },
      set() {},
    },
  },

  connected() {
    this.contentInputShadow = this.querySelector('.todo-content-input--shadow');
    this.contentInput = this.querySelector('.todo-content-input--editor');
    this.originalContent = this.contentInput.value;
    this.deleteTotally = false;
    this.edited = !!this.originalContent;

    this.listenTo(this.contentInput, {
      blur: this.handleContentInputBlur.bind(this),
      keydown: this.handleContentInputKeydown.bind(this),
      keyup: this.handleContentInputKeyup.bind(this),
      paste: this.handleContentInputPaste.bind(this),
    });

    this.listenTo(this.contentInput, 'input', throttle(() => {
      this.contentInputShadow.innerText = this.contentInput.value;
    }, 100));

    this.listenTo(this, {
      click: this.handleClick.bind(this),
      'tr-checkbox-field:change': this.toggleCompletionStatus.bind(this),
    });

    if (this.editing) this.beginEditing();

    let tid = 0;
    const contentShadow = this.querySelector('.todo-content-shadow');
    $(contentShadow).hover(() => {
      tid = setTimeout(() => {
        contentShadow.classList.add('hover');
      }, 800);
    }, () => {
      contentShadow.classList.remove('hover');
      clearTimeout(tid);
    });
  },

  preventClickEvent({ target }) {
    if (!this.contains(target)) return false;

    return (this.editable && target === this.contentInput)
      || closest(target, '.expandable-icon', this)
      || closest(target, '.drag-handle', this)
      || closest(target, '.completion-status-checkbox', this);
  },

  beginEditing() {
    if (!this.editable) return;

    this.editing = true;
    this.contentInput.value = this.originalContent;
    this.contentInput.focus();

    const { length: selectionOffset } = this.contentInput.value;
    this.contentInput.setSelectionRange(selectionOffset, selectionOffset);
  },

  triggerCellValueChange() {
    const { value } = this.contentInput;

    if (value !== this.originalContent) {
      this.namespacedTrigger('cellValueChange', {
        detail: { value },
        bubbles: true,
      });
    } else if (value) {
      this.editing = false;
    }
  },

  handleClick(event) {
    if (closest(event.target, '.drag-handle', this)
      || closest(event.target, '.completion-status-checkbox', this)) return;

    if (closest(event.target, '.open-in-tab', this)) {
      event.stopPropagation();
      this.namespacedTrigger('openInTab', {
        bubbles: true,
      });
      return;
    }

    if (closest(event.target, '.show-details', this) && window.tower.metaKey(event)) {
      event.stopPropagation();
      this.namespacedTrigger('openInCurrent', {
        bubbles: true,
      });
    }

    if (closest(event.target, '.todo-content-shadow.hover', this)) {
      this.beginEditing();
    }

    if (closest(event.target, '.expandable-icon', this)) {
      event.stopPropagation();
      this.namespacedTrigger('expandRow', { bubbles: true });
    }
  },

  toggleCompletionStatus() {
    const isCompleted = !this.completed;

    this.completed = isCompleted;
    this.namespacedTrigger('completedChange', {
      detail: { completed: isCompleted },
      bubbles: true,
    });
  },

  handleContentInputBlur() {
    if (this.skipInputBlurHandler) {
      this.skipInputBlurHandler = false;
      return;
    }

    this.triggerCellValueChange();
  },

  handleContentInputKeyup(event) {
    const isBackspace = event.key === 'Backspace' || event.keyCode === 8;
    if (isBackspace && !this.value) {
      this.deleteTotally = true;
    }
  },

  handleContentInputKeydown(event) {
    if (event.isComposing || event.keyCode === 229) return;

    const isEnter = event.key === 'Enter' || event.keyCode === 13;
    const isEscape = event.key === 'Escape' || event.keyCode === 27;
    const isBackspace = event.key === 'Backspace' || event.keyCode === 8;

    if (!this.edited && !isEnter && !isEscape && !isBackspace) {
      this.edited = true;
    }

    if (isEnter) {
      event.preventDefault();
      event.stopPropagation();

      this.skipInputBlurHandler = true;
      this.triggerCellValueChange();
      this.namespacedTrigger('enter', { bubbles: true });
    } else if (isEscape) {
      event.preventDefault();
      event.stopPropagation();

      this.skipInputBlurHandler = true;
      this.editing = false;
      this.namespacedTrigger('escape', { bubbles: true });
    } else if (isBackspace && !this.value && (this.deleteTotally || !this.edited)) {
      event.preventDefault();
      event.stopPropagation();
      this.namespacedTrigger('backspace', { bubbles: true });
      this.deleteTotally = false;
    }
  },

  handleContentInputPaste(event) {
    const content = event.clipboardData.getData('text');
    if (!/\n/.test(content)) return;

    event.preventDefault();
    event.stopPropagation();

    this.triggerCellValueChange();
    this.namespacedTrigger('pasteMultiLine', {
      detail: { content },
      bubbles: true,
    });
  },
});
